import * as yup from "yup";
import React from 'react';
import PersonalDetails from "./steps/PersonalDetails";
import Memberships from "./steps/Memberships";
import {t} from "i18next";
import Payment from "./steps/Payment";
import {DB_DATE_FORMAT} from "@arboxappv4/shared/src/constants/constants";
import {store} from "@arboxappv4/shared/src/redux/store";
import Success from "./steps/Success";
import {flowOptions, getSuccessProp} from "./FlowOptions";
import Waiver from "./steps/waiver/Waiver";
import {
    questionTypes,
    signatureTypes,
    signatureTypesEnum,
    bookingTypes,
    membershipTypes, customFieldFlowNames, sessionTypes
} from "@arboxappv4/shared/src/helpers/constants";
import {CONST} from "@arboxappv4/shared/src/constants/Forms";
import dayjs from "dayjs";
import SingleItemPage from "./steps/SingleItemPage";
import {
    checkIfHPGroupDisplay, formatPhoneToCountryCodeFormat,
    getHPGroupIdFromLocation, getRelevantStore,
    isUserFieldRequired,
    isValidIsraeliID
} from "../helpers/functions";
import GroupSessions from "./steps/groupSessions/GroupSessions";
import ServiceTypeSelection from "./steps/appointment/ServiceTypeSelection";
import AvailabilitySlots from "./steps/appointment/AvailabilitySlots";
import Courses from "./steps/courses/Courses";
import Shop from "./steps/shop/Shop";
import ChildDetails from "./steps/courses/ChildDetails";
import ChildSelection from "./steps/courses/ChildSelection";
import TrialPersonalDetails from "./steps/TrialPersonalDetails";
import {isArray} from "lodash";
import LeadForm from "./steps/LeadForm";
import {
    GROUP_FORM_SETTINGS,
    HUGIM_FORM_SETTINGS,
    SITE_LEAD_FORM_SETTINGS,
    USER_SITE_FIELDS_SETTINGS
} from "../../../src/constants";
import { isValidPhoneNumber } from 'react-phone-number-input'
import ArrivalConfirmation from "./steps/ArrivalConfirmation";
import {getGroupFormId, getHugFormId, getTrialFlowFormId} from "../helpers/FlowFormHelpers";

export const stepOptions = {
    PERSONAL_DETAILS: 'PERSONAL_DETAILS',
    SELECT_MEMBERSHIP: 'SELECT_MEMBERSHIP',
    DIRECT_ITEM: 'DIRECT_ITEM',
    WAIVER: 'WAIVER',
    PAYMENT: 'PAYMENT',
    SUCCESS: 'SUCCESS',
    GROUP_SESSIONS: 'GROUP_SESSIONS',
    GROUP_SERVICE_TYPE: 'GROUP_SERVICE_TYPE',
    SERVICE_TYPE: 'SERVICE_TYPE',
    AVAILABILITY_SLOT: 'AVAILABILITY_SLOT',
    COURSES: 'COURSES',
    SHOP: 'SHOP',
    COURSE_PARENT_DETAILS: 'COURSE_PARENT_DETAILS',
    COURSE_CHILD_DETAILS: 'COURSE_CHILD_DETAILS',
    COURSE_CHILD_SELECTION: 'COURSE_CHILD_SELECTION',
    TRIAL_GROUP_DETAILS: 'TRIAL_GROUP_DETAILS',
    LEAD_FORM: 'LEAD_FORM',
    ARRIVAL_CONFIRMATION: 'ARRIVAL_CONFIRMATION'
}

export const stepValidationSchema = () => ({
    PERSONAL_DETAILS: personalDetailsValidation(),
    SELECT_MEMBERSHIP: yup.object().shape({
        continueWithoutMembership: yup.boolean().required(),
        cart: yup.array().nullable().when("continueWithoutMembership", {
            is: false,
            then: () => yup.array(yup.object().shape({
                id: yup.number().required(),
                start: yup.string().required(t('mandatory-field'))
            })),
            otherwise: () => yup.array().nullable()
        }),
    }),
    WAIVER: waiverValidation(),
    PAYMENT: yup.object().shape({
        payment_info: yup.object().required(),
    }),
    DIRECT_ITEM: yup.object().shape({
        cart: yup.array(yup.object().shape({
            id: yup.number().required(),
            start: yup.string().required(t('mandatory-field'))
        })),
        locations_box_fk: yup.number().positive().required(t('mandatory-field')),
    }),
    SUCCESS: yup.object().shape({}),
    GROUP_SESSIONS: yup.object().shape({
        action: yup.string().oneOf([bookingTypes.INSERT_STAND_BY, bookingTypes.INSERT_SCHEDULE_USER, bookingTypes.CANCEL_SCHEDULE_USER, bookingTypes.CANCEL_WAIT_LIST]),
        schedule: yup.object().shape({
            id: yup.number().required()
        }),
        id: yup.number().nullable().when("action", {
            is: action => action === bookingTypes.INSERT_STAND_BY || action === bookingTypes.INSERT_SCHEDULE_USER,
            then: () => yup.number().required()
        }),
        payForSlot: yup.boolean().nullable().when("action", {
            is: action => action === bookingTypes.INSERT_STAND_BY || action === bookingTypes.INSERT_SCHEDULE_USER,
            then: () => yup.boolean().required()
        }),
    }),
    GROUP_SERVICE_TYPE: yup.object().shape({
        category_group_id: yup.number().required()
    }),
    SERVICE_TYPE: yup.object().shape({
        service: yup.object().shape({
            id: yup.number().required()
        })
    }),
    AVAILABILITY_SLOT: yup.object().shape({
        schedule: yup.object().shape({
            id: yup.number().required()
        }),
        id: yup.number().required(),
        payForSlot: yup.boolean().required(),
    }),
    COURSES: yup.object().shape({
        course: yup.object().shape({
            id: yup.number().required()
        }),
        serviceId: yup.number().required()
    }),
    SHOP: yup.object().shape({
        cart: yup.array(yup.object().shape({
            id: yup.number().required(),
        })),
        locations_box_fk: yup.number().positive().required(t('mandatory-field')),
    }),
    COURSE_PARENT_DETAILS: personalDetailsValidation(),
    COURSE_CHILD_DETAILS: yup.object().shape({
        child: courseChildDetailsValidation()
    }),
    COURSE_CHILD_SELECTION: yup.object().shape({
        isNewChild: yup.boolean().required(),
        child: yup.object().when('isNewChild',{
            is: true,
            then: () => courseChildDetailsValidation(),
            otherwise: () => yup.object().shape({
                child_id: yup.number().required(),
            })
        })
    }),
    TRIAL_GROUP_DETAILS: personalDetailsValidation().concat(yup.object().shape({
        cart: yup.array(yup.object().shape({
            id: yup.number().required(),
        })),
        locations_box_fk: yup.number().positive().required(t('mandatory-field')),
        schedule: yup.object().shape({
            id: yup.number().required()
        }),
    })),
    LEAD_FORM: personalDetailsValidation()
})

export const stepInitialValues = (params = null) => {
    return {
        PERSONAL_DETAILS: personalDetailsInitialValues(params),
        SELECT_MEMBERSHIP: {
            cart: params?.membership ? (isArray(params?.membership) ? params?.membership : [params?.membership]) : null,
            continueWithoutMembership: false
        },
        WAIVER: waiverInitialValue(params),
        PAYMENT: { payment_info: null },
        DIRECT_ITEM: {
            cart: params?.target ? [params?.target] : params?.membership ? (isArray(params?.membership) ? params?.membership : [params?.membership]) : null,
            locations_box_fk: getRegisterLocation(params?.target ?? null),
            continueWithoutMembership: false
        },
        SUCCESS: {},
        GROUP_SESSIONS: {
            schedule: params?.target ?? null,
            id: null,
            payForSlot: null,
            action: params?.target?.booking_option ?? null
        },
        GROUP_SERVICE_TYPE: {
            category_group_id: null,
        },
        SERVICE_TYPE: {
            service: null
        },
        AVAILABILITY_SLOT: params?.target ? {service: params.target, locations_box_fk: params.location} : {},
        COURSES: {
            course: params?.target ?? null, //basically only for direct page
            serviceId: null,
        },
        SHOP: {
            cart: params?.target ? [params?.target] : null,
            locations_box_fk: params?.target?.location_box_fk ?? null,
        },
        COURSE_PARENT_DETAILS: personalDetailsInitialValues(params),
        COURSE_CHILD_DETAILS: {
            child: {
                first_name: '',
                last_name: '',
                phone: '',
                birthday: null,
                personal_id: '',
                gender: ''
            }
        },
        COURSE_CHILD_SELECTION: {
            isNewChild: false,
            child: {}
        },
        TRIAL_GROUP_DETAILS: getTrialSessionInitialValues(params),
        LEAD_FORM: personalDetailsInitialValues(params)
}}

export const renderStep = {
    PERSONAL_DETAILS: (params) => {
        const flowName = store.getState().stepper.flowName
        const displayHPGroups = checkIfHPGroupDisplay() && flowName === flowOptions.REGISTER
        let hideLocation = params?.locationParams?.membership && !displayHPGroups ? params?.locationParams?.membership : ([flowOptions.GROUP_SESSION, flowOptions.DIRECT_GROUP_SESSION, flowOptions.STAFF_APPOINTMENT, flowOptions.SPACE_APPOINTMENT, flowOptions.COURSE, flowOptions.DIRECT_COURSE, flowOptions.PRODUCT_PURCHASE, flowOptions.DIRECT_STAFF_APPOINTMENT, flowOptions.DIRECT_SPACE_APPOINTMENT].includes(flowName));
        let selectedLocationGroupId = null
        const cfFlowName = flowName === flowOptions.COURSE ? customFieldFlowNames.BOOK_TO_HUG_SITE : null
        if(params?.locationParams?.membership && displayHPGroups) {
            const membership = isArray(params?.locationParams?.membership) ? params?.locationParams?.membership[0] : params?.locationParams?.membership
            selectedLocationGroupId = getHPGroupIdFromLocation(membership.location_box_fk)
            hideLocation = false; //NEW - allow register to other location in the same hp group
        }
        return <PersonalDetails hideLocation={hideLocation} HPGroupId={selectedLocationGroupId} cfFlowName={cfFlowName} {...params}/>
    },
    SELECT_MEMBERSHIP: (params) => {
        let additional = {}
        const flowName = store.getState().stepper.flowName
        const isRegisterWithHPGroup = flowName === flowOptions.REGISTER && params?.values?.locations_box_fk && checkIfHPGroupDisplay()
        additional.skipStep = params?.locationParams?.membership ?? false;
        additional.checkIfNoMembershipAllowed = flowName === flowOptions.REGISTER;
        additional.HPGroupDisplay = flowName === flowOptions.MEMBERSHIP_PURCHASE || isRegisterWithHPGroup;
        additional.hideLocation = !!params?.values?.locations_box_fk && flowName !== flowOptions.MEMBERSHIP_PURCHASE
        additional.showCart = flowName === flowOptions.MEMBERSHIP_PURCHASE;
        additional.HPGroupId = isRegisterWithHPGroup ? getHPGroupIdFromLocation(params?.values?.locations_box_fk) : null
        return <Memberships {...additional} {...params}/>
    },
    WAIVER: (params) => {
        const flowName = store.getState().stepper.flowName
        let additional = {}
        if([flowOptions.COURSE, flowOptions.DIRECT_COURSE].includes(flowName)) {
            additional.formId = getHugFormId()
        } else if([flowOptions.DIRECT_TRIAL_GROUP].includes(flowName)) {
            const isHug = params?.target?.box_categories?.category_type?.name === sessionTypes.HUGIM
            additional.formId = getTrialFlowFormId(isHug ? HUGIM_FORM_SETTINGS : GROUP_FORM_SETTINGS)
        } else if([flowOptions.GROUP_SESSION, flowOptions.DIRECT_GROUP_SESSION].includes(flowName)) {
            additional.formId = getGroupFormId(GROUP_FORM_SETTINGS)
        }
        return <Waiver {...additional} {...params}/>
    },
    PAYMENT: (params) => {
        const relevantStore = getRelevantStore()
        const flowName = relevantStore.stepper?.flowName
        let additional = {}
        let idPropOverride = null
        if([flowOptions.GROUP_SESSION, flowOptions.DIRECT_GROUP_SESSION, flowOptions.STAFF_APPOINTMENT, flowOptions.SPACE_APPOINTMENT, flowOptions.DIRECT_STAFF_APPOINTMENT, flowOptions.DIRECT_SPACE_APPOINTMENT].includes(flowName)) {
            idPropOverride = 'id'
            additional.startOverride = params.values.schedule?.date
        }
        if([flowOptions.COURSE, flowOptions.DIRECT_COURSE].includes(flowName)) {
            const course = relevantStore.stepper.flags.courseInfo
            idPropOverride = 'serviceId'
            const courseStartDate = dayjs(course?.start_date, DB_DATE_FORMAT);
            const today = dayjs()
            if(course?.allow_mid_booking && (today.isAfter(courseStartDate, 'day') || course?.past)) {
                additional.startOverride = today.format(DB_DATE_FORMAT)
            }
        }
        return <Payment idPropOverride={idPropOverride} {...additional} {...params}/>
    },
    DIRECT_ITEM: (params) => (<SingleItemPage {...params}/>),
    SUCCESS: (params) => (<Success {...getSuccessProp(params)}/>),
    GROUP_SESSIONS: (params) => {
        const flowName = store.getState().stepper.flowName
        return <GroupSessions isDirectSession={flowName === flowOptions.DIRECT_GROUP_SESSION} {...params}/>
    },
    GROUP_SERVICE_TYPE: (params) => {
        const flowName = store.getState().stepper.flowName
        const isCoachAppointment = flowName === flowOptions.STAFF_APPOINTMENT
        return <ServiceTypeSelection isCoachAppointment={isCoachAppointment} isGroupDisplay={true} {...params}/>
    },
    SERVICE_TYPE: (params) => {
        const flowName = store.getState().stepper.flowName
        const steps = store.getState().stepper.steps
        const isCoachAppointment = flowName === flowOptions.STAFF_APPOINTMENT
        const hideLocation = steps.includes(stepOptions.GROUP_SERVICE_TYPE)
        return <ServiceTypeSelection isCoachAppointment={isCoachAppointment} hideLocation={hideLocation} isGroupDisplay={false} {...params}/>
    },
    AVAILABILITY_SLOT: (params) => {
        const flowName = store.getState().stepper.flowName
        const isCoachAppointment = flowName === flowOptions.STAFF_APPOINTMENT || flowName === flowOptions.DIRECT_STAFF_APPOINTMENT;
        return <AvailabilitySlots isCoachAppointment={isCoachAppointment} {...params}/>
    },
    COURSES: (params) => {
        const flowName = store.getState().stepper.flowName
        return <Courses isDirectCourse={flowName === flowOptions.DIRECT_COURSE} {...params}/>
    },
    SHOP: (params) => {
        const flowName = store.getState().stepper.flowName
        const isOneTimeLink = params?.target?.isOneTimeLink ?? false
        return <Shop isDirectItem={flowName === flowOptions.DIRECT_PRODUCT_PURCHASE} isOneTimeLink={isOneTimeLink} {...params}/>
    },
    COURSE_PARENT_DETAILS: (params) => {
        const additional = {
            hideLocation: true,
            helperText: t('course-parent-helper-text'),
            cfFlowName: customFieldFlowNames.COURSE_PARENT
        }
        return <PersonalDetails {...additional} {...params}/>
    },
    COURSE_CHILD_DETAILS: (params) => {
        const additional = {
            cfFlowName: customFieldFlowNames.COURSE_CHILD
        }
        return <ChildDetails {...additional} {...params}/>
    },
    COURSE_CHILD_SELECTION: (params) => {
        const additional = {
            cfFlowName: customFieldFlowNames.COURSE_CHILD
        }
        return <ChildSelection {...additional} {...params}/>
    },
    TRIAL_GROUP_DETAILS: (params) => {
        return <TrialPersonalDetails isLead={true} {...params}/>
    },
    LEAD_FORM: (params) => {
        return <LeadForm {...params}/>
    },
    ARRIVAL_CONFIRMATION: (params) => {
        return <ArrivalConfirmation {...params} />
    }
}

export const stepHeader = ({initProps, values}) => ({
    PERSONAL_DETAILS: () => t('create-account'),
    SELECT_MEMBERSHIP: () => t('select-membership'),
    WAIVER: () => t('waiver'),
    PAYMENT: () => t('payment-header'),
    DIRECT_ITEM: () => initProps?.target?.name ?? '',
    SUCCESS: () => '',
    GROUP_SESSIONS: () => {
        const flowName = store.getState().stepper.flowName
        return flowName === flowOptions.GROUP_SESSION ? t('group-sessions') : initProps?.target?.box_categories.name
    },
    GROUP_SERVICE_TYPE: () => {
        const flowName = store.getState().stepper.flowName
        const isCoachAppointment = flowName === flowOptions.STAFF_APPOINTMENT
        return t(`select-group-service-type-${isCoachAppointment? 'staff' : 'space'}`)
    },
    SERVICE_TYPE: () => {
        const flowName = store.getState().stepper.flowName
        const isCoachAppointment = flowName === flowOptions.STAFF_APPOINTMENT
        return t(`select-service-type-${isCoachAppointment? 'staff' : 'space'}`)
    },
    AVAILABILITY_SLOT: () => values.service?.name,
    COURSES: () => {
        const flowName = store.getState().stepper.flowName
        return [flowOptions.COURSE].includes(flowName) ? t('courses') : initProps?.target?.box_categories.name
    },
    SHOP: () => {
        const flowName = store.getState().stepper.flowName
        return flowName === flowOptions.PRODUCT_PURCHASE ? t('products') : initProps?.target?.name
    },
    COURSE_PARENT_DETAILS: () => t('course-parent-details'),
    COURSE_CHILD_DETAILS: () => t('course-child-details'),
    COURSE_CHILD_SELECTION: () => t('course-child-selection'),
    TRIAL_GROUP_DETAILS: () => initProps?.target?.box_categories.name,
    LEAD_FORM: () => t('lead-form-header'),
    ARRIVAL_CONFIRMATION: () => t('arrival-confirmation-header')
})

export const onBack = {
    PERSONAL_DETAILS: () => null,
    SELECT_MEMBERSHIP: () => null,
    WAIVER: (params) => {
        if(params.setFieldValue) {
            // params.setFieldValue('digitalFormInfo', {form: null, digital_form: null})
        }
    },
    PAYMENT: () => null,
    DIRECT_ITEM: () => null,
    SUCCESS: () => null,
    GROUP_SESSIONS: () => null,
    GROUP_SERVICE_TYPE: () => null,
    SERVICE_TYPE: (params) => {
        if(params.setFieldValue && params.values?.category_group_id) {
            params.setFieldValue('category_group_id', null)
        }
    },
    AVAILABILITY_SLOT: (params) => {
        if(params.setFieldValue) {
            params.setFieldValue('service', null)
        }
    },
    COURSES: () => null,
    SHOP: () => null,
    COURSE_PARENT_DETAILS: () => null,
    COURSE_CHILD_DETAILS: () => null,
    COURSE_CHILD_SELECTION: () => (params) => {
        if(params.setFieldValue) {
            params.setFieldValue('isNewChild', false)
            params.setFieldValue('child', {})
        }
    },
    TRIAL_GROUP_DETAILS: () => null,
}

const conditionalUserDetailsSchema = (schema, boxSettings, field) => {
    if (boxSettings?.[field]?.required || boxSettings?.[field] === undefined) {
        return schema.required(t('thisFieldIsMandatory'))
    }
    return schema
}

const personalDetailsValidation = () =>
{
    const box = store.getState().site.box
    const flowName = store.getState().stepper.flowName
    const commonSchema = yup.object().shape({
        first_name: yup.string().trim().min(2, t('min-chars', {min: 2})).required(t('mandatory-field')),
        allow_mailing_list: yup.string().oneOf(['yes','no']),
        allow_sms: yup.string().oneOf(['yes','no']),
        locations_box_fk: yup.number().positive().required(t('mandatory-field'))
    })
    let schema;
    switch (flowName) {
        case flowOptions.LEAD_FORM:
            const leadFieldSettings = box?.boxes_settings?.find(setting => setting.prop_name === SITE_LEAD_FORM_SETTINGS)?.properties
            schema = commonSchema.concat(yup.object().shape({
                last_name: conditionalUserDetailsSchema(yup.string().trim().min(2, t('min-chars', {min: 2})), leadFieldSettings, 'last_name'),
                email: conditionalUserDetailsSchema(yup.string().email(), leadFieldSettings, 'email'),
                phone: conditionalUserDetailsSchema(yup.string().matches(/^(\+?\d{5,15})$/, t('invalid-phone')).min(5, t('min-chars', {min: 5})), leadFieldSettings, 'phone'),
                comment: conditionalUserDetailsSchema(yup.string(), leadFieldSettings, 'comment'),
            }))
            break;
        default:
            const userFieldSettings = box?.boxes_settings?.find(setting => setting.prop_name === USER_SITE_FIELDS_SETTINGS)?.properties
            const type = [flowOptions.DIRECT_TRIAL_GROUP].includes(flowName) ? 'lead' : 'member'
            schema = commonSchema.concat(yup.object().shape({
                last_name: yup.string().trim().min(2, t('min-chars', {min: 2})).required(t('mandatory-field')),
                email: yup.string().when([], {
                    is: () => isUserFieldRequired(userFieldSettings, 'email', type, true),
                    then: () => yup.string().email(t('email-invalid-format')).required(t('email-required-field')),
                    otherwise: () => yup.string().nullable()
                }),
                phone: yup.string().when([], {
                    is: () => isUserFieldRequired(userFieldSettings, 'phone', type, true),
                    then: () => yup.string().required(t('mandatory-field')).test('phone-validator', t('invalid-phone'), (value) => value && isValidPhoneNumber(value) === true),
                    otherwise: () => yup.string().nullable()
                }),
                birthday: yup.string().when([], {
                    is: () => isUserFieldRequired(userFieldSettings, 'birthday', type),
                    then: () => yup.string().required(t('mandatory-field')).test('date-format', 'invalid date', (value) => dayjs(value, DB_DATE_FORMAT).format(DB_DATE_FORMAT) === value),
                    otherwise: () => yup.string().nullable().test('date-format', 'invalid date', (value) => !value || (dayjs(value, DB_DATE_FORMAT).format(DB_DATE_FORMAT) === value))
                }),
                lead_id: yup.number().positive().nullable(),
                personal_id: yup.string().when([], {
                    is: () => (userFieldSettings && isUserFieldRequired(userFieldSettings, 'personal_id', type)) || (!userFieldSettings && box?.synced_items_rivhit),
                    then: () => yup.string().required(t('mandatory-field')).matches(/^[0-9]*$/, t('numbers-only')).test('length-test', t('personal-id-length-error'), val => !val || (val && (val.length === 0 || isValidIsraeliID(val)))), //check starts with 0 and then 9 digits or 972 and then
                    otherwise: () => yup.string().matches(/^[0-9]*$/, t('numbers-only')).test('length-test', t('personal-id-length-error'), val => !val || (val && (val.length === 0 || isValidIsraeliID(val))))
                }),
                gender: yup.string().when([], {
                    is: () => isUserFieldRequired(userFieldSettings, 'gender', type),
                    then: () => yup.string().oneOf(['male', 'female']).required(t('mandatory-field')),
                    otherwise: () => yup.string().nullable()
                }),
                customFields: getCustomFieldValueValidationSchema()
            }))
            break;
    }

    return schema;
}

const courseChildDetailsValidation = () => {
    const box = store.getState().site.box
    const userFieldSettings = box?.boxes_settings?.find(setting => setting.prop_name === USER_SITE_FIELDS_SETTINGS)?.properties
    const type = 'child'
    return yup.object().shape({
        first_name: yup.string().min(2, t('min-chars', {min: 2})).required(t('mandatory-field')),
        last_name: yup.string().min(2, t('min-chars', {min: 2})).required(t('mandatory-field')),
        phone: yup.string().when([], {
            is: () => isUserFieldRequired(userFieldSettings, 'phone', type),
            then: () => yup.string().required(t('mandatory-field')).matches(/^(\+?\d{5,15})$/, t('invalid-phone')),
            otherwise: () => yup.string().nullable().matches(/^(\+?\d{5,15})?$/, t('invalid-phone'))
        }),
        birthday: yup.string().when([], {
            is: () => isUserFieldRequired(userFieldSettings, 'birthday', type),
            then: () => yup.string().required(t('mandatory-field')).test('date-format', 'invalid date', (value) => dayjs(value, DB_DATE_FORMAT).format(DB_DATE_FORMAT) === value),
            otherwise: () => yup.string().nullable().test('date-format', 'invalid date', (value) => !value || (dayjs(value, DB_DATE_FORMAT).format(DB_DATE_FORMAT) === value))
        }),
        personal_id: yup.string().when([], {
            is: () => (userFieldSettings && isUserFieldRequired(userFieldSettings, 'personal_id', type)) || (!userFieldSettings && box?.synced_items_rivhit),
            then: () => yup.string().required(t('mandatory-field')).matches(/^[0-9]*$/, t('numbers-only')).test('length-test', t('personal-id-length-error'), val => !val || (val && (val.length === 0 || isValidIsraeliID(val)))), //check starts with 0 and then 9 digits or 972 and then
            otherwise: () => yup.string().matches(/^[0-9]*$/, t('numbers-only')).test('length-test', t('personal-id-length-error'), val => !val || (val && (val.length === 0 || isValidIsraeliID(val))))
        }),
        gender: yup.string().when([], {
            is: () => isUserFieldRequired(userFieldSettings, 'gender', type),
            then: () => yup.string().oneOf(['male', 'female']).required(t('mandatory-field')),
            otherwise: () => yup.string().nullable()
        }),
        customFields: getCustomFieldValueValidationSchema()
    })
}

const waiverValidation = () => {
    const asyncData = store.getState().stepper.asyncData
    if(asyncData && asyncData[stepOptions.WAIVER]) {
        return formValidation(asyncData[stepOptions.WAIVER])
    }
    return yup.object().shape({})
}

const formValidation = (formData) => {
    let formValidationSchema = yup.object().shape({})
    if(formData.digital_form.signature_options === signatureTypesEnum.CHECKBOX_AND_SIGNATURE || formData.digital_form.signature_options === signatureTypesEnum.SIGNATURE_ONLY) {
        formValidationSchema = formValidationSchema.concat(yup.object().shape({
            signature_data: yup.string().required(t('mandatory-field'))
        }))
    }
    if(formData.digital_form.signature_options === signatureTypesEnum.CHECKBOX_AND_SIGNATURE || formData.digital_form.signature_options === signatureTypesEnum.CHECKBOX_ONLY) {
        formValidationSchema = formValidationSchema.concat(yup.object().shape({
            agree: yup.boolean().oneOf([true],t('form-agree-checkbox-error')).required(t('mandatory-field'))
        }))
    }
    const questions = formData.digital_form_builder.find(type => type.property_type === "questions")
    if(questions) {
        formValidationSchema = formValidationSchema.concat(yup.object().shape({
            questions: yup.array(yup.object().shape({
                type: yup.string().oneOf([questionTypes.MULTI_CHOICE, questionTypes.OPEN, questionTypes.YES_NO, questionTypes.YES_NO_ADV]).required(),
                multi_choice: yup.boolean().required(),
                required: yup.boolean().required(),
                user_answer: yup.string().when(['type','multi_choice', 'required'], ([type, multi_choice, required], schema) => {
                    switch(type) {
                        case questionTypes.OPEN:
                            return required ? schema.required(t('mandatory-field')) : schema.nullable();
                        case questionTypes.YES_NO:
                        case questionTypes.YES_NO_ADV:
                            return required ? yup.string().required(t('mandatory-field')).oneOf(['true', 'false']) : yup.string().nullable().oneOf(['true', 'false']);
                        case questionTypes.MULTI_CHOICE:
                            if(multi_choice) {
                                return schema.nullable()
                            } else {
                                return required ? schema.required(t('mandatory-field')) : schema.nullable();
                            }
                        default:
                            return schema
                    }
                }),
                checked_array: yup.array().when(['type','multi_choice', 'required'], ([type, multi_choice, required], schema) => {
                    if(type === questionTypes.MULTI_CHOICE && multi_choice) {
                        return required ? schema.of(
                            yup.object().shape({
                                id: yup.number().required(),
                                checked: yup.boolean().required(),
                                answer_text: yup.string().required()
                            })).required(t('mandatory-field')).min(1) : schema
                    }
                    return schema.nullable()
                }),
            }))
        }))
    }
    return yup.object().shape({
        digitalFormInfo: yup.object().shape({
            form: formValidationSchema,
            digital_form: yup.object().shape({
                id: yup.number().required()
            })
        })
    })
}

const waiverInitialValue = (params = {}) => {
    const asyncData = store.getState().stepper.asyncData
    if(asyncData && asyncData[stepOptions.WAIVER]) {
        const form = asyncData[stepOptions.WAIVER]
        const questions = form.digital_form_builder.find((item => item.property_type === CONST.TYPE_QUESTIONS));
        const newQuestions = questions?.digital_form_question.reduce((acc, q) => {
            if(q.type === questionTypes.MULTI_CHOICE) {
                const newAnswers = q.answers.map(answer => ({...answer, checked: false}))
                return [...acc, {...q, answers: newAnswers}]
            } else {
                return [...acc, q]
            }
        }, [])
        const hasCheckbox = form.digital_form.signature_options === signatureTypesEnum.CHECKBOX_AND_SIGNATURE || form.digital_form.signature_options === signatureTypesEnum.CHECKBOX_ONLY
        const signed_props = form.digital_form_builder.find(type => type.property_type === CONST.TYPE_SIGNED_PROPERTIES)
        let signedProperties = {}
        if(signed_props) {
            const userBox = store.getState().user.activeUsersBoxes ?? params?.values ?? {}
            signed_props.digital_form_property.map(item => {
                const name_ref = item.name.replace(/([A-Z])/g, '_$1').trim().toLowerCase();
                signedProperties[item.name] = userBox.hasOwnProperty(name_ref) && userBox[name_ref] !== null ? userBox[name_ref] : null;
                if (CONST.TYPE_PICKERS.includes(item.name)) {
                    if (item.name === 'birthday') {
                        signedProperties.birthday = userBox.birthday
                    } else {
                        signedProperties.signatureDate = new Date();
                    }
                }
            })
        }
        return {
            digitalFormInfo: {
                form: {questions: newQuestions ? newQuestions : null, agree: !hasCheckbox, signedProperties},
                digital_form: form.digital_form
            }
        }
    }
    return {}
}

const personalDetailsInitialValues = (params) => {
    const flowName = store.getState().stepper.flowName
    const signInProps = store.getState().site?.signInProps
    const convertedLeadData = params?.convertedLeadData;
    const membership = params?.membership ? (isArray(params?.membership) ? params?.membership[0] : params?.membership) : null
    const signInIdentification = {phone: signInProps?.phone, email: signInProps?.email}

    let values
    switch (flowName) {
        case flowOptions.LEAD_FORM:
            values = {
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                comment: '',
                locations_box_fk: '',
                allow_sms: 'yes',
                allow_mailing_list: 'yes',
                ...signInIdentification
            }
            break;
        default:
            values = {
                first_name: convertedLeadData?.first_name ?? '',
                last_name: convertedLeadData?.last_name ?? '',
                email: convertedLeadData?.email ?? signInIdentification.email ?? '',
                phone: convertedLeadData?.phone || signInIdentification?.phone ? formatPhoneToCountryCodeFormat(convertedLeadData?.phone ?? signInIdentification.phone) : '',
                birthday: convertedLeadData?.birthday ?? null,
                allow_mailing_list: 'yes',
                allow_sms: 'yes',
                locations_box_fk: getRegisterLocation(membership ?? null),
                gender: convertedLeadData?.gender && ['male', 'female'].includes(convertedLeadData.gender) ? convertedLeadData.gender : '',
                lead_id: convertedLeadData?.user_fk ?? null,
                personal_id: '',
                isConvertedFromSite: !!convertedLeadData?.user_fk // aka: convertedLeadData?.user_fk ? true : false
            }
            break;
    }
    if([flowOptions.DIRECT_STAFF_APPOINTMENT, flowOptions.DIRECT_SPACE_APPOINTMENT].includes(flowName)) {
        values.locations_box_fk = params?.location
    }
    return values;
}

const getTrialSessionInitialValues = (params) => {
    let membership = params?.target?.series?.membership_types.find(item => item.type === membershipTypes.TRIAL)
    if(!membership) return {}
    membership = {...membership, start: params?.target?.date}
    return {
        ...personalDetailsInitialValues(params),
        schedule: params.target,
        cart: [membership],
        locations_box_fk: getRegisterLocation(membership),
    }
}

const getRegisterLocation = (membership = null) => {
    let membershipLocation = membership?.location_box_fk ?? null
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    const loggedIn = store.getState().auth.loggedIn
    const globalLocation = store.getState().site.selectedLocation

    if(membershipLocation && params.overrideLocation === 'true' && !loggedIn) {
        if(membership.payment_locations && membership.payment_locations.includes(globalLocation?.id)) {
            membershipLocation = globalLocation?.id
        }
    }
    return membershipLocation
}

const getCustomFieldValueValidationSchema = () => {
    return yup.array(yup.object().shape({
        value: yup.string().nullable()
            .when('props', {
                is: value => value.isRequired,
                then: schema => schema.required(t('mandatory-field'))
            })
            .when('custom_field_type', {
                is: value => value === 'phone',
                then: schema => schema.matches(/^(\+?\d{5,15})$/, t('invalid-phone')).min(5, t('min-chars', {min: 5})),
            }),
    }));

}
